import React, { Fragment, useContext, useEffect, useState, useMemo } from 'react';
import { Breadcrumbs } from '../../../AbstractElements';
import { getAllCustomersAPI } from '../../../Library/apis';
import { Container, Card, CardBody } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomerTable from './CustomerTable';
import UserContext from '../../../_helper/User';
import SubHeader from '../Common/SubHeader';
import FilterHeader from '../Common/FilterHeader';

const fields = [
  { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'e.g. John' },
  { key: 'lastName', label: 'Last Name', type: 'text', placeholder: 'e.g. Doe' },
  { key: 'userId', label: 'User ID', type: 'text', placeholder: 'e.g. 4000000000' },
  { key: 'phoneNumber', label: 'Phone Number', type: 'text', placeholder: 'e.g. 08012345678' },
  { key: 'email', label: 'Email', type: 'email', placeholder: 'e.g. user@email.com' },
];

const CustomerManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useContext(UserContext);
  const LIMIT = 20;

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  
  const [currentPage, setCurrentPage] = useState(Number(queryParams.get('page')) || 1);
  const [showDisabled, setShowDisabled] = useState(queryParams.get('disabled') === 'true');
  const [lastKnownPage, setLastKnownPage] = useState(Number(queryParams.get('page')) || 1);

  const [activeFilters, setActiveFilters] = useState({
    firstName: queryParams.get('firstName') || '',
    lastName: queryParams.get('lastName') || '',
    userId: queryParams.get('userId') || '',
    phoneNumber: queryParams.get('phoneNumber') || '',
    email: queryParams.get('email') || '',
  });

  // LOGIC: Check if any filter field has text in it
  const isSearchActive = Object.values(activeFilters).some(v => v !== "" && v !== undefined);

  const isHasRightRole = userInfo.roles && userInfo.roles.includes('Update Customer');

  const updateStateAndUrl = (newFilters, newPage, newDisabled) => {
    const params = new URLSearchParams();
    if (newPage > 1) params.set('page', newPage);
    if (newDisabled) params.set('disabled', 'true');
    
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) params.set(key, newFilters[key]);
    });

    navigate({ search: params.toString() }, { replace: true });
    
    setIsLoading(true);
    getAllCustomersAPI(
      { ...newFilters, showDisabled: newDisabled ? 'true' : 'false' },
      newPage,
      LIMIT
    )
      .then(res => {
        setCustomers(res.customers || []);
        setTotalRows(res.pagination?.total || 0);
        setCurrentPage(newPage);
        setActiveFilters(newFilters);
        setShowDisabled(newDisabled);

        const searching = Object.values(newFilters).some(v => v !== "" && v !== undefined);
        if (!searching) {
          setLastKnownPage(newPage);
        }
      })
      .catch(e => {
        console.error(e);
        setCustomers([]);
        setTotalRows(0);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    updateStateAndUrl(activeFilters, currentPage, showDisabled);
  }, []);

  const handleSearch = (currentFilters) => updateStateAndUrl(currentFilters, 1, showDisabled);
  const handlePageChange = (page) => updateStateAndUrl(activeFilters, page, showDisabled);
  const handleReset = () => updateStateAndUrl({}, lastKnownPage, showDisabled);
  
  // LOGIC: Prevent toggle if search is active
  const handleToggleDisabled = () => {
    if (isSearchActive) return; 
    updateStateAndUrl(activeFilters, 1, !showDisabled);
  };
  const handleRefresh = () => updateStateAndUrl(activeFilters, currentPage, showDisabled);
  return (
    <Fragment>
      <Breadcrumbs mainTitle='Customer Management' parent='Mothrift' title='Customer Management' />
      <Container fluid={true}>
        <SubHeader title='All Customers' data={customers} />

        <FilterHeader
          title="Filter Records"
          fields={fields}
          onSearch={handleSearch}
          onReset={handleReset}
          isLoading={isLoading}  
          onRefresh={handleRefresh}
          initialFilters={activeFilters} 
        />

        <div className='d-flex justify-content-end mb-3'>
          <div
            onClick={handleToggleDisabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              // Visual feedback: Dim the button and change cursor when disabled
              cursor: isSearchActive ? 'not-allowed' : 'pointer',
              opacity: isSearchActive ? 0.6 : 1,
              filter: isSearchActive ? 'grayscale(0.5)' : 'none',
              padding: '6px 16px',
              borderRadius: '20px',
              border: `1.5px solid ${showDisabled ? '#e53e3e' : '#e2e8f0'}`,
              backgroundColor: showDisabled ? '#fff5f5' : '#fff',
              color: showDisabled ? '#e53e3e' : '#718096',
              fontSize: '13px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              userSelect: 'none',
            }}
          >
            <div style={{
              width: '32px',
              height: '18px',
              borderRadius: '9px',
              backgroundColor: showDisabled ? '#e53e3e' : (isSearchActive ? '#e2e8f0' : '#cbd5e0'),
              position: 'relative',
              transition: 'background-color 0.2s ease',
            }}>
              <div style={{
                position: 'absolute',
                top: '2px',
                left: showDisabled ? '16px' : '2px',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                transition: 'left 0.2s ease',
              }} />
            </div>
            {showDisabled ? 'Showing Disabled' : 'Show Disabled'}
          </div>
        </div>

        <Card className='tranaction-card'>
          <CardBody className='pt-4'>
            <div className='table-responsive recent-table transaction-table'>
              <CustomerTable
                data={customers}
                updateAllCustomers={() => updateStateAndUrl(activeFilters, currentPage, showDisabled)}
                isEditable={isHasRightRole}
                isLoading={isLoading}
                totalRows={totalRows}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                limit={LIMIT}
              />
            </div>
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default CustomerManagement;